import { useNavigate } from "@tanstack/react-router";
import { IconAlertTriangle, IconArrowRight } from "@tabler/icons-react";
import { Alert, Button, Group, Stack, Text } from "@/components/ui";
import { useAuthStore } from "@/lib/auth-store";
import { useReenviarSolicitud } from "@/features/solicitudes/hooks/use-envio-autorizacion";

interface Props {
  solicitudId: string;
  /** Dueño de la solicitud · "Editar archivos"/"Reenviar" son exclusivos del
   *  dueño (403 del backend para cualquier otro) · Mesa de Control ve TODAS
   *  las solicitudes rechazadas de otros (decisión P29) sin poder actuar aquí. */
  solicitanteId: string;
  motivos: string[];
  nivelDelRechazo: number | null;
}

/**
 * Banner de "solicitud rechazada para corrección" · presente tanto en
 * el tab Autorización del detalle como en la vista "esperando autorización"
 * (cuando los tabs están ocultos). Botón "Reenviar al nivel N"
 * dispara la mutation correspondiente.
 */
export function RechazoBanner({ solicitudId, solicitanteId, motivos, nivelDelRechazo }: Props) {
  const navigate = useNavigate();
  const reenviar = useReenviarSolicitud(solicitudId);
  const user = useAuthStore((s) => s.user);
  const esDueno = user?.id === solicitanteId;

  return (
    <Alert
      variant="warning"
      icon={<IconAlertTriangle size={18} />}
      title={`Solicitud rechazada para corrección${nivelDelRechazo ? ` · nivel ${nivelDelRechazo}` : ""}`}
    >
      <Stack gap="sm" className="mt-1.5">
        <Text size="sm">
          {esDueno
            ? `Ajusta los archivos según los motivos y reenvíala · el flujo continuará desde el nivel ${nivelDelRechazo} (decisión 37 · las autorizaciones previas se conservan).`
            : "El solicitante debe ajustar los archivos y reenviarla."}
        </Text>
        {motivos.length > 0 && (
          <Stack gap={4}>
            <Text size="xs" fw={600}>Motivos:</Text>
            {motivos.map((m, i) => (
              <Text key={i} size="xs">• {m}</Text>
            ))}
          </Stack>
        )}
        {esDueno && (
          <Group justify="flex-end" gap="xs">
            <Button
              size="xs"
              variant="default"
              onClick={() => navigate({ to: "/solicitudes/$id/documentos", params: { id: solicitudId } })}
            >
              Editar archivos
            </Button>
            <Button
              size="xs"
              className="bg-advertencia-600 hover:bg-advertencia-700"
              loading={reenviar.isPending}
              onClick={() => reenviar.mutate()}
              rightSection={<IconArrowRight size={12} />}
            >
              {nivelDelRechazo ? `Reenviar al nivel ${nivelDelRechazo}` : "Reenviar"}
            </Button>
          </Group>
        )}
      </Stack>
    </Alert>
  );
}
